/**
 * plantArchetypes - care_growth_type → parametrische Low-Poly-Geometrie (#601)
 *
 * Reine, WebGL-freie Logik (keine React-/three-Imports): liefert pro
 * growth_type einen Primitiv-Bauplan (Baum = Stamm + Krone, Strauch =
 * Ellipsoid, …), skaliert mit den echten Wuchsdaten der platzierten Pflanze —
 * keine Asset-Bibliothek (PLAN_3D_VISUALISIERUNG_598.md §3.4).
 *
 * Zwei Ebenen:
 * 1. buildPlantSceneEntry(): serialisierte PlantCircle → SceneModel-Eintrag
 *    { id, growthType, archetype, position, heightM, crownDiameterM, seed, bloomColor }
 * 2. buildPlantInstanceGroups(): SceneModel-Pflanzen → Instanz-Gruppen pro
 *    (Geometrie-Shape × castsShadow × Layer) — PlantInstances.jsx rendert je
 *    Gruppe genau eine drei-<Instances>; 200+ Pflanzen = wenige Draw-Calls.
 *
 * Einheiten-Konvention der Instanzen: Unit-Geometrien (Durchmesser 1, Höhe 1,
 * am Ursprung zentriert) → scale = [BreiteM, HöheM, TiefeM] volle Ausdehnung.
 *
 * Blüh-Schicht: separat schaltbar (bloomVisible) und seit #610 monatsabhängig —
 * buildPlantInstanceGroups nimmt einen 0-basierten `month` (Garten-Monat aus
 * GardenTimeContext) und blendet Sprenkel/Farbkappen nur für Pflanzen ein,
 * die laut `floweringMonths` (1–12) dann blühen. Ohne Blühangabe bleibt die
 * Schicht sichtbar (neutraler Zustand, analog 2D „ohne Blühangabe ⇒
 * unverändert"); bulb-Köpfe bleiben immer (Silhouette), fallen ohne Blüte
 * aber auf Laubgrün zurück.
 *
 * Zeitregler-Naht: alle Größen laufen durch getPlantSizeAt(plant, tYears) —
 * v1 liefert immer „ausgewachsen", der Wachstums-Regler ersetzt später nur
 * tYears (Follow-up, Plan §7).
 *
 * WICHTIG (Enum-Konvention): Bei neuem care_growth_type-Wert müssen README,
 * validate_plant_csv.py, plantFilters.js UND dieses Modul nachgezogen werden —
 * der Jest-Guard vergleicht PLANT_ARCHETYPES gegen GROWTH_TYPE_LABELS.
 */

import { cmToM } from './units';
import { FLOWER_COLOR_MAP } from '../constants/flowerColors';

// ---------------------------------------------------------------------------
// Farben (gedeckte Töne, Markenton — vgl. materials3d.js)
// ---------------------------------------------------------------------------

/** 3 Laubgrün-Nuancen gegen Klon-Optik; Auswahl deterministisch aus Seed. */
export const FOLIAGE_COLORS = ['#7CA267', '#6B9457', '#86AD72'];

/** Dunklere Nadel-Töne für immergrüne Kronen (Koniferen). */
export const CONIFER_COLORS = ['#4F7A52', '#456E49', '#5A8560'];

export const TRUNK_COLOR = '#8A6B4F';
export const POT_COLOR = '#C4704F'; // Terracotta (vgl. MATERIAL_COLORS.terracotta)

// ---------------------------------------------------------------------------
// Archetyp-Spezifikationen — ein Eintrag pro care_growth_type (11 Werte)
// ---------------------------------------------------------------------------

/**
 * Typ-Default-Größen (cm) als letzte Stufe der Fallback-Kette und
 * castsShadow-Regel pro Archetyp (Bäume/Sträucher/Hohes wirft Schatten,
 * Bodennahes empfängt nur).
 */
export const PLANT_ARCHETYPES = {
  tree: { archetype: 'tree', castsShadow: true, defaultHeightCm: 800, defaultWidthCm: 500 },
  shrub: { archetype: 'shrub', castsShadow: true, defaultHeightCm: 200, defaultWidthCm: 150 },
  perennial: { archetype: 'tuft', castsShadow: false, defaultHeightCm: 60, defaultWidthCm: 40 },
  annual: { archetype: 'annual', castsShadow: false, defaultHeightCm: 40, defaultWidthCm: 30 },
  herb: { archetype: 'tuft', castsShadow: false, defaultHeightCm: 40, defaultWidthCm: 30 },
  vegetable: { archetype: 'tuft', castsShadow: false, defaultHeightCm: 50, defaultWidthCm: 40 },
  grass: { archetype: 'grass', castsShadow: false, defaultHeightCm: 80, defaultWidthCm: 50 },
  vine: { archetype: 'vine', castsShadow: true, defaultHeightCm: 250, defaultWidthCm: 60 },
  bulb: { archetype: 'bulb', castsShadow: false, defaultHeightCm: 35, defaultWidthCm: 15 },
  houseplant: { archetype: 'houseplant', castsShadow: false, defaultHeightCm: 60, defaultWidthCm: 40 },
  lawn: { archetype: null, castsShadow: false, defaultHeightCm: 3, defaultWidthCm: 30 },
};

const FALLBACK_GROWTH_TYPE = 'perennial'; // unbekannter Enum-Wert degradiert zum Büschel

// ---------------------------------------------------------------------------
// Determinismus: Hash + Pseudozufalls-Sequenz
// ---------------------------------------------------------------------------

/**
 * FNV-1a-32-Hash eines Strings → uint32. Deterministisch über Renders.
 * @param {string} str
 * @returns {number}
 */
export function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Seed einer platzierten Pflanze. plantId allein reicht nicht — mehrere
 * Exemplare derselben Art teilen die ID und sähen sonst klonhaft identisch
 * aus. Die gerundete Position (cm-genau) ist über Renders stabil und
 * unterscheidet Nachbarn.
 */
export function plantSeed(plantId, position) {
  const px = Math.round((position?.x ?? 0) * 100);
  const pz = Math.round((position?.z ?? 0) * 100);
  return hashString(`${plantId ?? 0}:${px}:${pz}`);
}

/** Kleine deterministische Float-Sequenz [0,1) aus einem Seed (mulberry32). */
export function seededSequence(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministische Laubfarbe aus dem Seed (2–3 Nuancen gegen Klon-Optik). */
export function pickFoliageColor(seed, palette = FOLIAGE_COLORS) {
  return palette[seed % palette.length];
}

// ---------------------------------------------------------------------------
// Wuchsdaten → Größe (Zeitregler-Naht)
// ---------------------------------------------------------------------------

/** Property flach oder unter plant.properties lesen (beide API-Formate). */
function plantProp(plant, key) {
  const flat = plant?.[key];
  if (flat !== undefined && flat !== null) return flat;
  return plant?.properties?.[key] ?? null;
}

function positiveNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** growth_type der Pflanze; unbekannte Werte fallen auf perennial zurück. */
export function resolveGrowthType(plant) {
  const raw = plantProp(plant, 'growth_type');
  return PLANT_ARCHETYPES[raw] ? raw : FALLBACK_GROWTH_TYPE;
}

/**
 * Größe der Pflanze zum Zeitpunkt tYears — v1 liefert immer „ausgewachsen".
 * Der Wachstums-Zeitregler (Follow-up) ersetzt später nur tYears.
 *
 * Fallback-Kette Höhe: max_height_cm → min_height_cm → Typ-Default.
 * Fallback-Kette Kronenbreite: max_width_cm → spacingCm → Typ-Default.
 *
 * @param {object} plant - plantProperties (flach oder mit .properties)
 * @param {number} [tYears=Infinity] - Zeitregler-Naht, v1 ungenutzt
 * @param {object} [options]
 * @param {number} [options.spacingCm] - Pflanzabstand der platzierten Instanz
 * @param {number} [options.heightCm] - Instanz-Override Höhe (#754), schlägt max_height_cm
 * @param {number} [options.widthCm] - Instanz-Override Breite (#754), schlägt max_width_cm
 * @returns {{heightM: number, crownDiameterM: number}}
 */
export function getPlantSizeAt(
  plant,
  tYears = Infinity,
  { spacingCm, heightCm: heightOverride, widthCm: widthOverride } = {}
) {
  void tYears; // v1: ausgewachsen — Parameter ist die Naht für den Zeitregler
  const growthType = resolveGrowthType(plant);
  const spec = PLANT_ARCHETYPES[growthType];

  // Per-instance override (#754) wins over the DB value: "welches Exemplar auf
  // dem bekannten Spektrum ist meins" (Rückschnitt / kleinere Variante).
  const heightCm =
    positiveNumber(heightOverride) ??
    positiveNumber(plantProp(plant, 'max_height_cm')) ??
    positiveNumber(plantProp(plant, 'min_height_cm')) ??
    spec.defaultHeightCm;

  const widthCm =
    positiveNumber(widthOverride) ??
    positiveNumber(plantProp(plant, 'max_width_cm')) ??
    positiveNumber(spacingCm) ??
    spec.defaultWidthCm;

  return { heightM: cmToM(heightCm), crownDiameterM: cmToM(widthCm) };
}

// ---------------------------------------------------------------------------
// Blütenfarbe
// ---------------------------------------------------------------------------

/**
 * Blütenfarbe als Hex: selectedColor der platzierten Pflanze gewinnt,
 * sonst erste bekannte DB-Blütenfarbe (flower_color-Array). null = keine.
 */
export function getBloomColorHex(plant, selectedColor) {
  if (selectedColor && FLOWER_COLOR_MAP[selectedColor]) {
    return FLOWER_COLOR_MAP[selectedColor].hex;
  }
  const dbColors = plantProp(plant, 'flower_color');
  if (Array.isArray(dbColors)) {
    const known = dbColors.find((c) => FLOWER_COLOR_MAP[c]);
    if (known) return FLOWER_COLOR_MAP[known].hex;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Snapshot → SceneModel-Eintrag
// ---------------------------------------------------------------------------

/**
 * SceneModel-Pflanzeneintrag aus einer serialisierten PlantCircle.
 * lawn (Bodenmaterial, keine Geometrie) und Einträge ohne plantProperties
 * liefern null.
 *
 * @param {object} obj - Snapshot-Eintrag (objectType 'plant')
 * @param {{x: number, z: number}} position - Weltposition (Meter)
 * @param {number} index - laufender Index für die Fallback-ID
 * @returns {object|null}
 */
export function buildPlantSceneEntry(obj, position, index) {
  const plant = obj?.plantProperties;
  if (!plant) return null;

  const growthType = resolveGrowthType(plant);
  const spec = PLANT_ARCHETYPES[growthType];
  if (!spec.archetype) return null; // lawn: keine Geometrie, Bodenmaterial

  const { heightM, crownDiameterM } = getPlantSizeAt(plant, Infinity, {
    spacingCm: obj.spacingCm,
    heightCm: obj.customHeightCm, // Instanz-Override (#754); null → DB-Default
    widthCm: obj.customWidthCm,
  });

  const evergreen = plantProp(plant, 'evergreen') === true;

  return {
    id: `plant-${obj.plantId ?? 'x'}-${index}`,
    growthType,
    archetype: spec.archetype,
    evergreen,
    position,
    heightM,
    crownDiameterM,
    seed: plantSeed(obj.plantId, position),
    bloomColor: getBloomColorHex(plant, obj.selectedColor),
    floweringMonths: normalizeFloweringMonths(plantProp(plant, 'flowering_months')),
    castsShadow: spec.castsShadow,
  };
}

/** flowering_months (1–12) als Zahlen-Array, oder null bei fehlender Angabe. */
function normalizeFloweringMonths(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const months = raw.map(Number).filter((m) => Number.isInteger(m) && m >= 1 && m <= 12);
  return months.length > 0 ? months : null;
}

/**
 * Blüht die Pflanze im 0-basierten Garten-Monat? Ohne Monat (null) oder ohne
 * Blühangabe bleibt die Blüh-Schicht sichtbar (neutraler Zustand, #610).
 */
function bloomsInMonth(floweringMonths, month) {
  if (month == null || floweringMonths == null) return true;
  return floweringMonths.includes(month + 1);
}

/**
 * Schatten-relevante Volumen einer Pflanze (#605), in Metern über der Basis.
 * Mehrere Teile pro Pflanze (Baum: Krone + Stamm), damit der 2D-Schatten die
 * 3D-Silhouette trifft — die Krone wird aus ihrer erhöhten Lage projiziert
 * (kein „Corona"-Ring am Stamm mehr), der Stamm wirft ein eigenes schmales Band.
 *
 * `disk` = runde Krone (Radius), `square` = Stamm-/Säulenquerschnitt (halbe
 * Kantenlänge). fromY/toY sind die Höhen, über die das Volumen reicht.
 *
 * @param {object} plant - SceneModel-Pflanzeneintrag (archetype/heightM/crownDiameterM/evergreen)
 * @returns {Array<{shape:'disk'|'square', radius?:number, halfSize?:number, fromY:number, toY:number}>}
 */
export function getPlantShadowVolumes(plant) {
  const H = plant.heightM;
  const W = plant.crownDiameterM;
  const radius = W / 2;

  switch (plant.archetype) {
    case 'tree': {
      const { crownBottomY, crownTopY } = treeCrownSpan(H, plant.evergreen);
      return [
        { shape: 'disk', radius, fromY: crownBottomY, toY: crownTopY },
        { shape: 'square', halfSize: treeTrunkDiameter(W) / 2, fromY: 0, toY: crownBottomY },
      ];
    }
    case 'shrub':
      // Körper sitzt auf kurzem Stummel (emitShrub: bodyBase = 0,1·H)
      return [{ shape: 'disk', radius, fromY: 0.1 * H, toY: H }];
    default:
      // vine u. a.: durchgehende Säule/Krone vom Boden
      return [{ shape: 'disk', radius, fromY: 0, toY: H }];
  }
}

// ---------------------------------------------------------------------------
// SceneModel-Pflanzen → Instanz-Gruppen (für drei <Instances>)
// ---------------------------------------------------------------------------

const MIN_DIM_M = 0.05; // degenerierte Wuchsdaten nicht auf 0 kollabieren lassen

/** Instanz an eine Gruppe anhängen (Gruppen-Key = Shape × Schatten × Layer). */
function pushInstance(groups, { shape, castsShadow, layer = 'plant' }, instance) {
  const key = `${shape}|${castsShadow ? 'cast' : 'nocast'}|${layer}`;
  let group = groups.get(key);
  if (!group) {
    group = { key, shape, castsShadow, layer, instances: [] };
    groups.set(key, group);
  }
  group.instances.push(instance);
}

// Sprenkel-Dichte (User-Feedback #601-Checkpoint, 2 Runden): Anzahl wächst
// mit der Hüllfläche — Fläche statt Volumen, weil die Sprenkel AUF der Hülle
// sitzen und so die optische Dichte unabhängig von der Pflanzengröße gleich
// bleibt. Runde 2: Dichte 3→8/m², Deckel 48→140 (3,6-m-Agave war weiter zu
// spärlich). 140 Instanzen × viele Pflanzen bleiben ein Draw-Call (instanziert);
// Mobile-Budget wird in #603 verifiziert.
export const SPRINKLES_MIN = 8;
export const SPRINKLES_MAX = 140;
export const SPRINKLES_PER_M2 = 8;

// Die Low-Poly-Hülle (sphere 8×6, flatShading) liegt zwischen ~0,89 (Facetten-
// Mitte) und 1,0 (Vertices) des mathematischen Radius. Sprenkel-Zentren sitzen
// in der Bandmitte; ihr Durchmesser wird an den LOKALEN Radius gekoppelt, so
// dass sie das Band immer durchstoßen — ein fixer Einrück-Faktor (Runde 2)
// vergrub sie sonst an den Polkappen hoher Ellipsoide (lokaler Radius dort am
// größten), nur die Äquator-Reihen blieben sichtbar (Runde-3-Befund).
const SPRINKLE_PLACEMENT = 0.95;
const SPRINKLE_MIN_DIAMETER_FACTOR = 0.13; // ≥ Facetten-Band (0,11) + Reserve

/** Gemeinsame Anzahl-/Größenformel aller Sprenkel-Varianten. */
function sprinkleSpec(surfaceM2, seed, sizeM) {
  return {
    count: Math.min(
      SPRINKLES_MAX,
      SPRINKLES_MIN + (seed % 4) + Math.round(surfaceM2 * SPRINKLES_PER_M2)
    ),
    // Große Hüllen bekommen etwas größere Sprenkel (lesbar aus Distanz)
    size: sizeM * (1 + 0.5 * Math.min(surfaceM2, 10) / 10),
  };
}

// --- Ikosaeder-Hülle (laubabwerfende Baumkronen) ----------------------------
// three.js IcosahedronGeometry-Basisdaten (detail 0). Für Kronen-Sprenkel wird
// der exakte Radius der facettierten Hülle in Sprenkel-Richtung berechnet:
// das Ikosaeder-Band (Inradius 0,795 bis Vertex 1,0) ist zu breit für den
// Mindestgrößen-Trick der 8×6-Kugel — Sprenkel säßen sonst vergraben oder
// schwebend. Konvexer Körper ⇒ r(d) = Inradius / max_i(d·n_i).
const ICOSA_T = (1 + Math.sqrt(5)) / 2;
const ICOSA_VERTICES = [
  [-1, ICOSA_T, 0], [1, ICOSA_T, 0], [-1, -ICOSA_T, 0], [1, -ICOSA_T, 0],
  [0, -1, ICOSA_T], [0, 1, ICOSA_T], [0, -1, -ICOSA_T], [0, 1, -ICOSA_T],
  [ICOSA_T, 0, -1], [ICOSA_T, 0, 1], [-ICOSA_T, 0, -1], [-ICOSA_T, 0, 1],
].map((v) => {
  const len = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / len, v[1] / len, v[2] / len];
});
const ICOSA_INDICES = [
  [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
  [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
  [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
  [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
];
const ICOSA_FACE_NORMALS = ICOSA_INDICES.map(([a, b, c]) => {
  const n = [
    ICOSA_VERTICES[a][0] + ICOSA_VERTICES[b][0] + ICOSA_VERTICES[c][0],
    ICOSA_VERTICES[a][1] + ICOSA_VERTICES[b][1] + ICOSA_VERTICES[c][1],
    ICOSA_VERTICES[a][2] + ICOSA_VERTICES[b][2] + ICOSA_VERTICES[c][2],
  ];
  const len = Math.hypot(n[0], n[1], n[2]);
  return [n[0] / len, n[1] / len, n[2] / len];
});
export const ICOSA_INRADIUS =
  ICOSA_FACE_NORMALS[0][0] * ICOSA_VERTICES[ICOSA_INDICES[0][0]][0] +
  ICOSA_FACE_NORMALS[0][1] * ICOSA_VERTICES[ICOSA_INDICES[0][0]][1] +
  ICOSA_FACE_NORMALS[0][2] * ICOSA_VERTICES[ICOSA_INDICES[0][0]][2];

/**
 * Radius der facettierten Ikosaeder-Hülle (Umkreis 1) in Richtung d
 * (Einheitsvektor [x, y, z]) — zwischen Inradius (~0,795) und 1.
 */
export function icosahedronRadiusInDirection(d) {
  let maxDot = 0;
  for (const n of ICOSA_FACE_NORMALS) {
    const dot = d[0] * n[0] + d[1] * n[1] + d[2] * n[2];
    if (dot > maxDot) maxDot = dot;
  }
  return ICOSA_INRADIUS / maxDot;
}

/**
 * Sprenkel (kleine Farbkugeln) deterministisch auf der Hülle eines Ellipsoids
 * (Breite W, Höhe von yBase bis yBase+bodyH) verteilen. polarMax steuert, wie
 * weit die Verteilung von der Spitze (0) Richtung Unterseite (π) reicht;
 * visibleFrac skaliert die Flächen-Heuristik (Halbkugel-Dome: 0.5).
 */
function emitSprinkles(groups, plant, rand, { yBase, bodyH, bodyW, sizeM, polarMax = 1.5, visibleFrac = 1 }) {
  const { position, bloomColor, id, seed } = plant;

  // Kugel-Näherung der Hüllfläche: π·D·H (exakt für die Kugel, gut genug hier)
  const surfaceM2 = Math.PI * bodyW * bodyH * visibleFrac;
  const { count, size: baseSize } = sprinkleSpec(surfaceM2, seed, sizeM);

  for (let i = 0; i < count; i++) {
    const azimuth = rand() * Math.PI * 2;
    const polar = rand() * polarMax;
    const rx = Math.sin(polar) * (bodyW / 2) * SPRINKLE_PLACEMENT;
    const y = yBase + bodyH / 2 + Math.cos(polar) * (bodyH / 2) * SPRINKLE_PLACEMENT;
    // Lokaler Ellipsoid-Radius in Sprenkel-Richtung → Mindestgröße, damit der
    // Sprenkel die Facette sicher durchstößt (weder vergraben noch schwebend)
    const rLocal = Math.sqrt(
      (Math.sin(polar) * (bodyW / 2)) ** 2 + (Math.cos(polar) * (bodyH / 2)) ** 2
    );
    const size = Math.max(baseSize, SPRINKLE_MIN_DIAMETER_FACTOR * rLocal);
    pushInstance(
      groups,
      { shape: 'sphere', castsShadow: false, layer: 'bloom' },
      {
        id: `${id}-sprinkle-${i}`,
        position: [position.x + Math.cos(azimuth) * rx, y, position.z + Math.sin(azimuth) * rx],
        scale: [size, size, size],
        color: bloomColor,
      }
    );
  }
}

/**
 * Vertikale Kronen-Struktur eines Baums (Meter über der Pflanzenbasis).
 * Single Point of Truth für emitTree (3D) UND getPlantShadowVolumes (#605
 * 2D-Schatten) — laubabwerfende Krone 0,25·H–H, immergrüne 0,15·H–H.
 */
export function treeCrownSpan(H, evergreen) {
  const crownH = evergreen ? H - 0.15 * H : 0.75 * H;
  const crownCenterY = H - crownH / 2;
  return {
    crownH,
    crownCenterY,
    crownBottomY: crownCenterY - crownH / 2,
    crownTopY: crownCenterY + crownH / 2,
  };
}

/** Stammdurchmesser eines Baums (Meter), gedeckelt. */
export function treeTrunkDiameter(W) {
  return Math.min(Math.max(0.1 * W, 0.06), 0.6);
}

/**
 * Baum: Zylinder-Stamm + Krone (Kegel = immergrün, Ikosaeder = laubabwerfend).
 * Laubabwerfende Kronen tragen bei Blütenfarbe Sprenkel (Apfelbaum & Co. —
 * Runde-3-Checkpoint: Bäume blühten gar nicht); Positionen sitzen exakt auf
 * der facettierten Ikosaeder-Hülle. Koniferen blühen nicht dekorativ.
 */
function emitTree(groups, plant, { bloomVisible }) {
  const { position, heightM: H, crownDiameterM: W, seed, evergreen, castsShadow, bloomColor, id } = plant;
  const trunkD = treeTrunkDiameter(W);
  const foliage = pickFoliageColor(seed, evergreen ? CONIFER_COLORS : FOLIAGE_COLORS);

  // Sichtbare Stammhöhe ≈ 20/30 % — die Geometrie reicht aber bis ins
  // Kronenzentrum: der facettierte Ikosaeder liegt bis zu ~20 % innerhalb
  // seiner mathematischen Hülle, bei großen Bäumen (Ahorn, 15 m) klaffte
  // sonst eine Lücke zwischen Stamm und Krone (#601-Checkpoint-Befund).
  const { crownH, crownCenterY } = treeCrownSpan(H, evergreen);
  const trunkH = crownCenterY + 0.05 * H;

  pushInstance(groups, { shape: 'cylinder', castsShadow }, {
    id: `${id}-trunk`,
    position: [position.x, trunkH / 2, position.z],
    scale: [trunkD, trunkH, trunkD],
    color: TRUNK_COLOR,
  });

  pushInstance(groups, { shape: evergreen ? 'cone' : 'icosahedron', castsShadow }, {
    id: `${id}-crown`,
    position: [position.x, crownCenterY, position.z],
    scale: [W, crownH, W],
    color: foliage,
  });

  if (!evergreen && bloomVisible && bloomColor) {
    const rand = seededSequence(seed);
    const { count, size } = sprinkleSpec(Math.PI * W * crownH, seed, 0.06);
    for (let i = 0; i < count; i++) {
      const azimuth = rand() * Math.PI * 2;
      const polar = rand() * 2.6; // ganze Krone bis auf die unterste Kappe
      const d = [
        Math.sin(polar) * Math.cos(azimuth),
        Math.cos(polar),
        Math.sin(polar) * Math.sin(azimuth),
      ];
      // exakter Facetten-Radius → Sprenkel sitzt halb eingebettet auf der Fläche
      const r = icosahedronRadiusInDirection(d);
      pushInstance(groups, { shape: 'sphere', castsShadow: false, layer: 'bloom' }, {
        id: `${id}-sprinkle-${i}`,
        position: [
          position.x + d[0] * r * (W / 2),
          crownCenterY + d[1] * r * (crownH / 2),
          position.z + d[2] * r * (W / 2),
        ],
        scale: [size, size, size],
        color: bloomColor,
      });
    }
  }
}

/** Strauch: Ellipsoid auf kurzem Stummel; Blüh-Sprenkel auf der Hülle. */
function emitShrub(groups, plant, { bloomVisible }) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, bloomColor, id } = plant;
  const stubH = 0.12 * H;
  const bodyH = 0.9 * H;
  const bodyBase = H - bodyH;

  pushInstance(groups, { shape: 'cylinder', castsShadow }, {
    id: `${id}-stub`,
    position: [position.x, stubH / 2, position.z],
    scale: [Math.max(0.08 * W, 0.03), stubH, Math.max(0.08 * W, 0.03)],
    color: TRUNK_COLOR,
  });
  pushInstance(groups, { shape: 'sphere', castsShadow }, {
    id: `${id}-body`,
    position: [position.x, bodyBase + bodyH / 2, position.z],
    scale: [W, bodyH, W],
    color: pickFoliageColor(seed),
  });

  if (bloomVisible && bloomColor) {
    const rand = seededSequence(plant.seed);
    emitSprinkles(groups, plant, rand, {
      yBase: bodyBase,
      bodyH,
      bodyW: W,
      sizeM: 0.05,
      polarMax: 2.2, // ganze Hülle bis auf die Unterseite
      visibleFrac: 0.9,
    });
  }
}

// Büschel-Einsenkung: voller Ellipsoid, um 5 % der Höhe in den Boden gesenkt.
// Runde-2-Checkpoint-Befund: der frühere Halbkugel-Trick (Kugel-Zentrum am
// Boden, untere Hälfte vergraben) hängte bei hohen Pflanzen (Ackerbohne 2 m)
// H Meter Geometrie unter den Boden — bei flachem Blickwinkel von außerhalb
// des Umrisses sichtbar („sticht durch den Grund").
const TUFT_SINK_FRACTION = 0.05;

/** Ellipsoid-Büschel (voll, leicht eingesenkt): Zentrum-y für Höhe bodyH. */
function tuftCenterY(bodyH) {
  return bodyH * (0.5 - TUFT_SINK_FRACTION);
}

/**
 * Büschel (perennial/herb/vegetable): gedrungener Ellipsoid, unten leicht
 * eingesenkt — Oberkante ≈ Wuchshöhe, kaum Untergrund-Geometrie.
 */
function emitTuft(groups, plant, { bloomVisible, sprinkles = true }) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, bloomColor, id, growthType } = plant;

  pushInstance(groups, { shape: 'sphere', castsShadow }, {
    id: `${id}-tuft`,
    position: [position.x, tuftCenterY(H), position.z],
    scale: [W, H, W],
    color: pickFoliageColor(seed),
  });

  // Blüh-Sprenkel nur für Staude/Kraut (Plan §3.4) — Gemüse blüht nicht dekorativ
  if (bloomVisible && bloomColor && sprinkles && growthType !== 'vegetable') {
    const rand = seededSequence(seed);
    emitSprinkles(groups, plant, rand, {
      yBase: tuftCenterY(H) - H / 2,
      bodyH: H,
      bodyW: W,
      sizeM: 0.035,
      polarMax: 1.6, // Hülle bis knapp unter den Äquator
      visibleFrac: 0.6,
    });
  }
}

/** Sommerblume (annual): flacher Teppich + großflächige Farbkappe + Sprenkel. */
function emitAnnual(groups, plant, { bloomVisible }) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, bloomColor, id } = plant;
  const centerY = tuftCenterY(H);

  pushInstance(groups, { shape: 'sphere', castsShadow }, {
    id: `${id}-tuft`,
    position: [position.x, centerY, position.z],
    scale: [W, H, W],
    color: pickFoliageColor(seed),
  });

  if (bloomVisible && bloomColor) {
    // Farbkappe: schmalerer, leicht höherer Ellipsoid stößt oben durch das Grün
    pushInstance(groups, { shape: 'sphere', castsShadow: false, layer: 'bloom' }, {
      id: `${id}-cap`,
      position: [position.x, centerY, position.z],
      scale: [0.9 * W, H * 1.12, 0.9 * W],
      color: bloomColor,
    });
    const rand = seededSequence(seed);
    emitSprinkles(groups, plant, rand, {
      yBase: centerY - H / 2,
      bodyH: H,
      bodyW: W,
      sizeM: 0.03,
      polarMax: 1.6,
      visibleFrac: 0.6,
    });
  }
}

/** Gras: 2–3 versetzte schmale Kegel; Versatz deterministisch rotiert. */
function emitGrass(groups, plant) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, id } = plant;
  const foliage = pickFoliageColor(seed);
  const rot = ((seed % 360) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const blades = [
    { dx: 0, dz: 0, h: H, w: 0.45 * W },
    { dx: 0.22 * W, dz: 0.08 * W, h: 0.75 * H, w: 0.35 * W },
    { dx: -0.18 * W, dz: -0.14 * W, h: 0.6 * H, w: 0.3 * W },
  ];
  blades.forEach((b, i) => {
    pushInstance(groups, { shape: 'cone', castsShadow }, {
      id: `${id}-blade-${i}`,
      position: [
        position.x + b.dx * cos - b.dz * sin,
        b.h / 2,
        position.z + b.dx * sin + b.dz * cos,
      ],
      scale: [b.w, b.h, b.w],
      color: foliage,
    });
  });
}

/** Kletterpflanze: schmale vertikale Säule (Rankhilfe angedeutet). */
function emitVine(groups, plant) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, id } = plant;
  const d = Math.min(0.3, Math.max(0.4 * W, 0.1));
  pushInstance(groups, { shape: 'cylinder', castsShadow }, {
    id: `${id}-column`,
    position: [position.x, H / 2, position.z],
    scale: [d, H, d],
    color: pickFoliageColor(seed),
  });
}

// Zwiebelblumen-Maße (Runde-4-Checkpoint „Allium wirkt kaputt"):
// Dolden-Durchmesser folgt der Wuchshöhe (0,16·H), ist aber beidseitig
// gedeckelt — Allium giganteum trägt ~15–20 cm, kein 25-cm-Ballon.
// Die Untergrenze greift erst unter ~9 cm Wuchshöhe: bei 2,5 cm fraß sie
// den ganzen 4-cm-Pflanzkreis der Traubenhyazinthe auf, die dadurch auf
// einen Einzelkopf zusammenfiel (deren Traube misst real ~1,5–2 cm).
const BULB_HEAD_MAX_D = 0.2;
const BULB_HEAD_MIN_D = 0.015;
const BULB_MAX_STEMS = 7;
// Mindest-Mittenabstand zweier Köpfe in Kopf-Durchmessern. < 1 hieße
// Überlappung — genau die Klumpen-Optik, die repariert wird.
const BULB_HEAD_CLEARANCE = 1.15;

/**
 * Wie viele Blütenstiele passen nebeneinander in den Pflanzkreis?
 *
 * Vorher war die Zahl frei gewürfelt (3–7) — bei großen Zwiebelblumen
 * verschmolzen die Köpfe zu einem Klumpen (25-cm-Köpfe auf einem 14-cm-Kreis).
 * Jetzt ergibt sie sich aus der Geometrie: die Köpfe sitzen auf einem Ring,
 * der sie gerade noch im Pflanzkreis hält (ringR = W/2 − headD/2), und dürfen
 * sich dort nicht berühren. Ergebnis: große Allium = 1 Dolde (botanisch
 * richtig, eine Zwiebel = ein Blütenstand), kleine Zwiebeln (Krokus,
 * Traubenhyazinthe) = dichter Horst.
 *
 * @param {number} headD - Kopf-Durchmesser (m)
 * @param {number} W - Kronendurchmesser/Pflanzkreis (m)
 * @returns {number} 1 … BULB_MAX_STEMS
 */
export function bulbStemCount(headD, W) {
  const ringR = W / 2 - headD / 2;
  if (ringR <= 0) return 1;
  // Sehnenbedingung: 2·ringR·sin(π/n) ≥ clearance·headD
  const s = (BULB_HEAD_CLEARANCE * headD) / (2 * ringR);
  if (s >= 1) return 1;
  return Math.max(1, Math.min(BULB_MAX_STEMS, Math.floor(Math.PI / Math.asin(s))));
}

/**
 * Zwiebelblume: flache Blattrosette + Zylinder-Stiele mit je einer Blütendolde
 * obenauf (ikonische Allium-/Tulpen-Silhouette). Die Rosette kam mit dem
 * #601-Checkpoint dazu — nackte Stiele wurden großen Zwiebelpflanzen nicht
 * gerecht; sie ist seit Runde 4 breit und flach statt hochkant (ein 63 cm
 * hohes, 32 cm schmales Ei umschloss die Stiele wie ein Kokon).
 *
 * Köpfe gehören zur Blüh-Schicht: ohne Blütenfarbe oder bei
 * bloomVisible:false bleiben sie als Laubgrün stehen (Silhouette bleibt).
 * Sie sind Ikosaeder, keine UV-Kugeln — bei 8×6 Segmenten kniff der Pol
 * sichtbar ein, die 20-Flächen-Kugel liest sich als saubere Dolde.
 */
function emitBulb(groups, plant, { bloomVisible }) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, bloomColor, id } = plant;
  const rand = seededSequence(seed);
  const foliage = pickFoliageColor(seed);
  const headColor = bloomVisible && bloomColor ? bloomColor : foliage;
  const headD = Math.min(Math.max(0.16 * H, BULB_HEAD_MIN_D), BULB_HEAD_MAX_D);
  // Stiel-Dicke folgt dem Kopf, nicht der Höhe: 0,03·H ergab bei 1,8 m einen
  // 4-cm-Mast, echte Allium-Schäfte messen ~2 cm.
  const stemD = Math.min(Math.max(0.12 * headD, 0.008), 0.03);

  // Blattrosette (eingesenkter Ellipsoid, breiter als hoch) — Zwiebelblumen
  // blühen nicht aus nacktem Boden, das Laub sitzt als flacher Horst unten
  const rosetteH = Math.min(0.3 * H, 0.22);
  const rosetteW = Math.max(1.1 * W, 0.1);
  pushInstance(groups, { shape: 'sphere', castsShadow }, {
    id: `${id}-leaves`,
    position: [position.x, tuftCenterY(rosetteH), position.z],
    scale: [rosetteW, rosetteH, rosetteW],
    color: foliage,
  });

  const stemCount = bulbStemCount(headD, W);
  // Einzelstiel steht mittig in der Rosette; mehrere verteilen sich gleichmäßig
  // auf dem Ring. Zufalls-Azimute klumpten — die Variation zwischen Nachbar-
  // pflanzen kommt jetzt aus dem Startwinkel, nicht aus dem Abstand (der ist
  // per bulbStemCount gerade so bemessen, dass sich nichts überlappt).
  const ringR = stemCount === 1 ? 0 : Math.max(W / 2 - headD / 2, 0);
  const azimuth0 = rand() * Math.PI * 2;

  for (let i = 0; i < stemCount; i++) {
    const azimuth = azimuth0 + (i * 2 * Math.PI) / stemCount;
    const x = position.x + Math.cos(azimuth) * ringR;
    const z = position.z + Math.sin(azimuth) * ringR;
    // Kopf-Oberkante trifft die Wuchshöhe (vorher blieb sie 15–35 % darunter);
    // die Reststreuung staffelt Nachbarpflanzen leicht gegeneinander.
    const headCenterY = Math.max(H * (0.94 + rand() * 0.06) - headD / 2, 0.02);
    // Stiel läuft bis ins Doldenzentrum, nicht bis zur Kugelunterkante: die
    // Ikosaeder-Facette liegt bei ~0,8 des Radius, an der Unterseite klaffte
    // sonst eine Lücke zwischen Stiel und Kopf (analog emitTree/Stamm).
    const stemH = headCenterY;

    pushInstance(groups, { shape: 'cylinder', castsShadow }, {
      id: `${id}-stem-${i}`,
      position: [x, stemH / 2, z],
      scale: [stemD, stemH, stemD],
      color: foliage,
    });
    pushInstance(groups, { shape: 'icosahedron', castsShadow, layer: 'bloom' }, {
      id: `${id}-head-${i}`,
      position: [x, headCenterY, z],
      scale: [headD, headD, headD],
      color: headColor,
    });
  }
}

/** Zimmer-/Kübelpflanze: Terracotta-Topf + Laubkugel, Mini-Farbkappe. */
function emitHouseplant(groups, plant, { bloomVisible }) {
  const { position, heightM: H, crownDiameterM: W, seed, castsShadow, bloomColor, id } = plant;
  const potH = 0.35 * H;
  const bodyH = 0.6 * H;

  pushInstance(groups, { shape: 'cylinder', castsShadow }, {
    id: `${id}-pot`,
    position: [position.x, potH / 2, position.z],
    scale: [0.5 * W, potH, 0.5 * W],
    color: POT_COLOR,
  });
  pushInstance(groups, { shape: 'sphere', castsShadow }, {
    id: `${id}-body`,
    position: [position.x, potH + bodyH / 2, position.z],
    scale: [0.85 * W, bodyH, 0.85 * W],
    color: pickFoliageColor(seed),
  });

  if (bloomVisible && bloomColor) {
    const rand = seededSequence(seed);
    emitSprinkles(groups, plant, rand, {
      yBase: potH,
      bodyH,
      bodyW: 0.85 * W,
      sizeM: 0.03,
      polarMax: 1.6,
      visibleFrac: 0.7,
    });
  }
}

const ARCHETYPE_EMITTERS = {
  tree: emitTree,
  shrub: emitShrub,
  tuft: (groups, plant, opts) => emitTuft(groups, plant, opts),
  annual: emitAnnual,
  grass: emitGrass,
  vine: emitVine,
  bulb: emitBulb,
  houseplant: emitHouseplant,
};

/**
 * Gruppiert alle Pflanzen des SceneModels zu Instanz-Gruppen für das
 * instanzierte Rendering.
 *
 * @param {Array<object>} plants - SceneModel.plants
 * @param {object} [options]
 * @param {boolean} [options.bloomVisible=true] - Blüh-Schicht (Naht für #270)
 * @returns {Array<{key, shape, castsShadow, layer, instances}>}
 */
export function buildPlantInstanceGroups(plants, { bloomVisible = true, month = null } = {}) {
  const groups = new Map();

  for (const raw of plants || []) {
    const emitter = ARCHETYPE_EMITTERS[raw.archetype];
    if (!emitter) continue;
    // degenerierte Wuchsdaten abfangen, bevor Skalen 0 werden
    const plant = {
      ...raw,
      heightM: Math.max(raw.heightM, MIN_DIM_M),
      crownDiameterM: Math.max(raw.crownDiameterM, MIN_DIM_M),
    };

    // #610: Blüh-Schicht pro Pflanze monatsabhängig — der 3D-Juni blüht
    // anders als der 3D-April.
    const plantBloomVisible = bloomVisible && bloomsInMonth(raw.floweringMonths ?? null, month);

    // #602: Geländeniveau — die Rezepte rechnen alle y ab 0; statt 11 Emitter
    // anzufassen, werden die in diesem Lauf erzeugten Instanzen zentral um
    // baseY gehoben (Längen-Snapshot der Gruppen vor/nach dem Emitter).
    const baseY = raw.baseY ?? 0;
    if (baseY === 0) {
      emitter(groups, plant, { bloomVisible: plantBloomVisible });
      continue;
    }
    const lengthsBefore = new Map();
    for (const group of groups.values()) {
      lengthsBefore.set(group.key, group.instances.length);
    }
    emitter(groups, plant, { bloomVisible: plantBloomVisible });
    for (const group of groups.values()) {
      for (let i = lengthsBefore.get(group.key) ?? 0; i < group.instances.length; i++) {
        group.instances[i].position[1] += baseY;
      }
    }
  }

  return Array.from(groups.values());
}
